import React, { Suspense, useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router';
import useApi from '../hooks/useApi';
import buildUrl from '../buildUrl';
import Dashboard from './views/Dashboard';
import Automation from './views/Automation';
import AutomationEmailEditor from './views/AutomationEmailEditor';
import UploadHtml from '../editor/UploadHtml';
import UploadImages from '../editor/UploadImages';
import NewAutomationEmail from './views/NewAutomationEmail';
import CreateOrEditAutomationDialog from './dialogs/CreateOrEditAutomationDialog';
import TogglePublishDialog from './dialogs/TogglePublishDialog';
import ConfirmDeleteDialog from './dialogs/ConfirmDeleteDialog';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import useSuspenseFetch from '../hooks/useSuspenseFetch';
import SplashScreen from '../splashScreen/SplashScreen';

const dialogs = {
    CREATE_AUTOMATION: 1,
    EDIT_AUTOMATION: 2,
    TOGGLE_PUBLISH_AUTOMATION: 3,
    DELETE_AUTOMATION: 4
};

const AutomationsInner = () => {
    const navigate = useNavigate();
    const { handleGet, handlePut, handlePost, handleDelete } = useApi();
    const { data: user } = useSuspenseFetch('user');

    const [isInitialising, setIsInitialising] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [allContactsCount, setAllContactsCount] = useState(null);
    const [dialog, setDialog] = useState(null);
    const [automationToEdit, setAutomationToEdit] = useState(null);
    const [automationToPublish, setAutomationToPublish] = useState(null);
    const [automationToDelete, setAutomationToDelete] = useState(null);
    const [allAutomations, setAllAutomations] = useState([]);
    const [availableTriggers, setAvailableTriggers] = useState([]);
    const [groups, setGroups] = useState([]);

    const hasManageAutomationPermission = user.permissions.includes('Manage Automations');

    const handleFetchAutomations = async () => {
        const response = await handleGet('automations');

        if (!response.ok) {
            console.error(response?.error);
            setIsLoading(false);
            return;
        }

        const automations = await response.json();

        setAllAutomations(automations);
        setIsLoading(false);
    };

    const handleCreateAutomation = async automation => {
        setIsLoading(true);

        const response = await handlePost('automations', automation);

        if (!response.ok) {
            console.error(response?.error);
            setIsLoading(false);
            return;
        }

        const newAutomation = await response.json();

        await handleFetchAutomations();
        navigate(`/automations/${newAutomation.id}`);
        setIsLoading(false);
    };

    const handleDeleteAutomation = async id => {
        setIsLoading(true);
        const response = await handleDelete(`automations/${id}`);

        if (!response.ok) {
            console.error(response?.error);
            setIsLoading(false);
            return;
        }

        navigate('/automations');
        await handleFetchAutomations();
        setIsLoading(false);
    };

    const handleUpdateAutomation = async automation => {
        setIsLoading(true);

        const response = await handlePut(`automations/${automation.id}`, automation);

        if (!response.ok) {
            console.error(response?.error);
            setIsLoading(false);
            return;
        }

        await handleFetchAutomations();

        setIsLoading(false);
    };

    const handleFetchTriggers = async () => {
        const response = await handleGet('automations/triggers');

        if (!response.ok) {
            console.error(response?.error);
            return;
        }

        const availableTriggers = await response.json();
        setAvailableTriggers(availableTriggers);
    };

    const handleFetchGroups = async () => {
        const response = await handleGet('groups');

        if (!response.ok) {
            console.error(response?.error);
            return;
        }

        const groups = (await response.json());
        setGroups(groups);
    };

    const handleFetchContactsCount = async () => {
        const params = {
            unsubscribed: false,
            bounced: false
        };

        const url = buildUrl('contacts/count', params);
        const response = await handleGet(url);

        if (!response.ok) {
            console.error(response?.error);
            return;
        }

        const count = await response.json();

        setAllContactsCount(count);
    };

    const handleInit = async () => {
        await handleFetchTriggers();
        await handleFetchAutomations();
        await handleFetchGroups();
        await handleFetchContactsCount();
        setIsInitialising(false);
    };

    // dialogs

    useEffect(() => {
        setDialog(automationToEdit ? dialogs.EDIT_AUTOMATION : null);
    }, [automationToEdit]);

    useEffect(() => {
        setDialog(automationToDelete ? dialogs.DELETE_AUTOMATION : null);
    }, [automationToDelete]);

    useEffect(() => {
        setDialog(automationToPublish ? dialogs.TOGGLE_PUBLISH_AUTOMATION : null);
    }, [automationToPublish]);

    useEffect(() => {
        if (hasManageAutomationPermission) {
            handleInit();
        }
    }, []);

    if (!hasManageAutomationPermission) {
        return (
            <Paper sx={{ padding: 6, textAlign: 'center' }}>
                <Typography>You don't have permission to manage Automations.</Typography>
                <Typography>Please contact your account administrator to have this enabled.</Typography>
            </Paper>
        );
    }

    return (
        <>
            <Routes>
                <Route path="/" element={(
                    <Dashboard
                        allAutomations={allAutomations}
                        groups={groups}
                        availableTriggers={availableTriggers}
                        isInitialising={isInitialising}
                        isLoading={isLoading}
                        onCreateAutomation={() => setDialog(dialogs.CREATE_AUTOMATION)}
                        onSetAutomationToEdit={setAutomationToEdit}
                        onSetAutomationToDelete={setAutomationToDelete}
                        onSetAutomationToPublish={setAutomationToPublish}
                    />
                )} />
                <Route path=":automationId" element={(
                    <>
                        {isLoading && (
                            <LoadingOverlay />
                        )}
                        <Automation
                            automations={allAutomations}
                            availableTriggers={availableTriggers}
                            onOpenEditAutomationDialog={setAutomationToEdit}
                            onOpenDeleteAutomationDialog={setAutomationToDelete}
                            onOpenConfirmPublishOrDraftAutomationDialog={setAutomationToPublish}
                            onFetchAutomations={handleFetchAutomations}
                        />
                    </>
                )} />
                <Route path=":automationId/emails/new" element={<NewAutomationEmail />} />
                <Route path=":automationId/emails/:emailId/upload" element={<UploadHtml />} />
                <Route path=":automationId/emails/:emailId/upload-images" element={<UploadImages />} />
                <Route path=":automationId/emails/:emailId" element={<AutomationEmailEditor />} />
            </Routes>
            {dialog === dialogs.CREATE_AUTOMATION && (
                <CreateOrEditAutomationDialog
                    onClose={() => setDialog(null)}
                    onSubmit={handleCreateAutomation}
                    isLoading={isLoading}
                    availableTriggers={availableTriggers}
                    allContactsCount={allContactsCount}
                />
            )}
            {(automationToEdit && dialog === dialogs.EDIT_AUTOMATION) && (
                <CreateOrEditAutomationDialog
                    onClose={() => setAutomationToEdit(null)}
                    onSubmit={handleUpdateAutomation}
                    isLoading={isLoading}
                    availableTriggers={availableTriggers}
                    allContactsCount={allContactsCount}
                    automation={automationToEdit}
                />
            )}
            {(automationToPublish && dialog === dialogs.TOGGLE_PUBLISH_AUTOMATION) && (
                <TogglePublishDialog
                    automation={automationToPublish}
                    onClose={() => setAutomationToPublish(null)}
                    onSubmit={handleUpdateAutomation}
                    availableTriggers={availableTriggers}
                />
            )}
            {(automationToDelete && dialog === dialogs.DELETE_AUTOMATION) && (
                <ConfirmDeleteDialog
                    onClose={() => setAutomationToDelete(null)}
                    onConfirm={handleDeleteAutomation}
                    item={automationToDelete}
                    type="Automation"
                />
            )}
        </>
    );
};

// todo the following is needed because React Router 7 requires each route to have its own Suspense boundary
// see https://github.com/remix-run/react-router/issues/10568
// inner component can be avoided by only loading resources where they are needed instead of drilling them like this

const Automations = () => {
    return (
        <Suspense fallback={<SplashScreen />}>
            <AutomationsInner />
        </Suspense>
    );
};

export default Automations;